<script setup>
// RM-427: one application question, drawn the way the sign-up form has always
// drawn it (same fields, styling and validation), from the question library's
// definition. Used by the sign-up form and the Admin preview.
import { computed } from 'vue'
import { emailValidator, phoneValidator, requiredValidator, urlValidator } from '@validators'
import FavoriteRestaurantsPicker from '@core/components/application/FavoriteRestaurantsPicker.vue'
import { NORMALIZERS, formatPhone } from '@/utils/applicationForm'

const props = defineProps({
  question: { type: Object, required: true },
  modelValue: { type: [String, Array, null], default: null },
  errorMessages: { type: [Array, String], default: () => [] },
  disabled: { type: Boolean, default: false },
  persistentHint: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

const q = computed(() => props.question)

const value = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
})

const rules = computed(() => {
  const list = []
  if (q.value.required)
    list.push(requiredValidator)
  if (q.value.type === 'email')
    list.push(emailValidator)
  if (q.value.type === 'phone')
    list.push(phoneValidator)
  if (q.value.type === 'url')
    list.push(v => !v || urlValidator(v))

  return list
})

const onPhoneInput = () => {
  value.value = formatPhone(value.value)
}

const onBlur = () => {
  const normalize = NORMALIZERS[q.value.format]
  if (normalize && value.value)
    value.value = normalize(value.value)
}

const fieldProps = {
  variant: 'solo-filled',
  flat: true,
  rounded: 'lg',
  'bg-color': 'grey-lighten-4',
  density: 'comfortable',
  class: 'mb-3',
}

const hint = computed(() => props.persistentHint || q.value.hint || '')
</script>

<template>
  <FavoriteRestaurantsPicker
    v-if="q.type === 'restaurants'"
    v-model="value"
    :label="q.label"
    :required="q.required"
    :error-messages="errorMessages"
  />
  <VSelect
    v-else-if="q.type === 'yesno' || q.type === 'select'"
    v-model="value"
    v-bind="fieldProps"
    :items="q.type === 'yesno' ? ['Yes', 'No'] : q.options"
    :rules="rules"
    :menu-props="{ location: 'bottom' }"
    :label="q.label"
    :hint="hint"
    :persistent-hint="!!hint"
    :error-messages="errorMessages"
    :disabled="disabled"
  />
  <VTextarea
    v-else-if="q.type === 'textarea'"
    v-model="value"
    v-bind="fieldProps"
    autocomplete="off"
    :rules="rules"
    rows="3"
    :label="q.label"
    :hint="hint"
    :persistent-hint="!!hint"
    :error-messages="errorMessages"
    :disabled="disabled"
  />
  <VTextField
    v-else-if="q.type === 'phone'"
    v-model="value"
    v-bind="fieldProps"
    autocomplete="off"
    :rules="rules"
    :label="q.label"
    type="tel"
    :placeholder="q.placeholder || '(555) 123-4567'"
    :hint="hint"
    :persistent-hint="!!hint"
    :error-messages="errorMessages"
    :disabled="disabled"
    @input="onPhoneInput"
  />
  <VTextField
    v-else
    v-model="value"
    v-bind="fieldProps"
    autocomplete="off"
    :rules="rules"
    :label="q.label"
    :type="q.type === 'email' ? 'email' : 'text'"
    :placeholder="q.placeholder || undefined"
    :hint="hint"
    :persistent-hint="!!hint"
    :error-messages="errorMessages"
    :disabled="disabled"
    @blur="onBlur"
  />
</template>
